import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useSearchParams } from "react-router-dom";
import { CheckCircle2, HandCoins, UserCheck } from "lucide-react";

import {
  Button,
  EmptyState,
  ErrorState,
  Money,
  Notice,
  Pagination,
  Select,
  SkeletonRows,
  Status,
  cn,
  focusRing,
} from "../../../../components/ui";
import { financeAPI } from "../../../services/businessErpService";
import PeriodDecisionDialog from "./PeriodDecisionDialog";
import { HandoverDialog, OnBehalfDialog } from "./SalaryDialogs";
import {
  PAYMENTS_PATH,
  SALARY_STATE,
  dateRange,
  formatDateTime,
  periodName,
  periodStatus,
} from "./payrollFinance";

const PERIOD_PAGE_SIZE = 2;
const SALARY_PAGE_SIZE = 10;

/** What happened to this salary so far, in one line. */
function salaryDetail(item) {
  const p = item.payment;
  if (item.state === "not_released") return "HR has not released this payslip yet.";
  if (item.state === "no_cash_due") return "Net pay is ₱0, so there is nothing to hand over or confirm.";
  if (!p) return null;
  const handed = `Handed over ${formatDateTime(p.handedOverAt)}${p.handedOverByName ? ` by ${p.handedOverByName}` : ""}${p.handoverReference ? ` · ref. ${p.handoverReference}` : ""}.`;
  if (item.state === "awaiting_employee") {
    if (item.formerEmployee) return `${handed} They no longer have access to My HR; the owner can confirm for them.`;
    if (item.suspended) return `${handed} They cannot sign in to My HR while suspended; the owner can confirm for them.`;
    return `${handed} The employee confirms receipt in My HR.`;
  }
  if (item.state === "paid_confirmed_by_owner") {
    return `${handed} Confirmed by the owner on the employee's behalf on ${formatDateTime(p.acknowledgedAt)}. Reason: ${p.onBehalfReason}`;
  }
  return `${handed} Confirmed by the employee on ${formatDateTime(p.acknowledgedAt)}.`;
}

// The salaries API reports payslipId 0 until a payment row exists (a joined column
// shadows it; backlog APP-FIN-013), so rows are keyed by their financial record.
const salaryKey = (item) => (item.recordId ? `r${item.recordId}` : `p${item.payslipId}-${item.employeeName}`);

const ROW_GRID = "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 md:grid-cols-[minmax(0,1fr)_8.5rem_12rem_12.5rem]";

/** One employee's salary: who, how much, where it stands, what Finance does next. */
function SalaryRow({ item, canRecordPayments, isOwner, onHandover, onBehalf }) {
  const meta = SALARY_STATE[item.state] || SALARY_STATE.not_released;
  const detail = salaryDetail(item);
  let action = null;
  if (item.state === "ready_for_claim" && canRecordPayments) {
    action = <Button size="sm" icon={HandCoins} onClick={() => onHandover(item)}>Record handover</Button>;
  } else if (item.state === "ready_for_claim") {
    action = <span className="text-meta text-secondary">Someone who records payments hands this over.</span>;
  } else if (item.state === "awaiting_employee" && isOwner) {
    action = <Button size="sm" variant="quiet" icon={UserCheck} onClick={() => onBehalf(item)}>Confirm on their behalf</Button>;
  }

  return (
    <li className={cn(ROW_GRID, "px-4 py-3 text-cell")}>
      <div className="min-w-0">
        <p className="truncate text-body font-medium text-foreground">{item.employeeName}</p>
        {item.formerEmployee ? <p className="text-meta text-tertiary">Former employee</p>
          : item.suspended ? <p className="text-meta text-tertiary">Suspended</p> : null}
      </div>
      <Money value={item.netPayCents} className="text-right text-money text-foreground" />
      <div className="min-w-0">
        <Status tone={meta.tone} icon={meta.tone === "success"}>{meta.label}</Status>
      </div>
      <div className="flex justify-end">{action}</div>
      {detail ? <p className="col-span-full text-meta text-secondary">{detail}</p> : null}
    </li>
  );
}

/** One released payroll and its salaries, with the settlement step once every salary is paid. */
function PeriodSalaries({ period, entry, canRecordPayments, isOwner, onRetry, onHandover, onBehalf, onSettle }) {
  const [salaryPage, setSalaryPage] = useState(1);
  const name = periodName(period);
  const headingId = `pay-${period.id}`;
  const summary = entry?.data?.summary;
  const salaries = useMemo(
    () => [...(entry?.data?.salaries || [])].sort((a, b) =>
      (SALARY_STATE[a.state]?.order ?? 9) - (SALARY_STATE[b.state]?.order ?? 9) || a.employeeName.localeCompare(b.employeeName)),
    [entry]
  );
  const salaryTotalPages = Math.max(1, Math.ceil(salaries.length / SALARY_PAGE_SIZE));
  const visibleSalaries = salaries.slice((salaryPage - 1) * SALARY_PAGE_SIZE, salaryPage * SALARY_PAGE_SIZE);

  useEffect(() => {
    setSalaryPage((current) => Math.min(current, salaryTotalPages));
  }, [salaryTotalPages]);

  return (
    <section aria-labelledby={headingId} className="space-y-3">
      <header className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div>
          <h2 id={headingId} className="text-section-title text-foreground">{name}</h2>
          <p className="text-body-sm text-secondary">{dateRange(period)}</p>
        </div>
        {summary ? (
          <p className="text-body-sm tabular-nums text-secondary">
            {summary.paid} of {summary.total - Number(summary.noCashDue || 0)} paid
            {summary.noCashDue ? ` · ${summary.noCashDue} with no cash due` : ""}
            {summary.outstandingCents ? <> · <Money value={summary.outstandingCents} /> still to pay</> : null}
          </p>
        ) : null}
      </header>

      {summary?.allPaid ? (
        <Notice
          tone="success"
          title={summary.paid ? "Every salary is paid" : "No cash is due in this payroll"}
          action={canRecordPayments ? <Button size="sm" icon={CheckCircle2} onClick={() => onSettle(period)}>Mark settled</Button> : null}
        >
          {canRecordPayments
            ? "Mark the payroll settled to close it."
            : "Someone who records payments can now mark the payroll settled."}
        </Notice>
      ) : null}

      <div className="rounded-panel border border-default bg-surface">
        {!entry || entry.state === "loading" ? (
          <SkeletonRows rows={3} columns={4} label={`Loading salaries for ${name}`} />
        ) : entry.state === "error" ? (
          <ErrorState title="Salaries couldn't be loaded" onRetry={() => onRetry(period.id)} />
        ) : !salaries.length ? (
          <EmptyState title="No salaries in this payroll" />
        ) : (
          <>
            <div aria-hidden="true" className={cn(ROW_GRID, "hidden border-b border-subtle bg-surface-sunken px-4 py-2 text-cell-head text-secondary md:grid")}>
              <span>Employee</span>
              <span className="text-right">Net pay</span>
              <span>State</span>
              <span className="text-right">Next step</span>
            </div>
            <ul aria-label={`Salaries for ${name}`} className="divide-y divide-subtle">
              {visibleSalaries.map((item) => (
                <SalaryRow
                  key={salaryKey(item)}
                  item={item}
                  canRecordPayments={canRecordPayments}
                  isOwner={isOwner}
                  onHandover={(salary) => onHandover(salary, period)}
                  onBehalf={(salary) => onBehalf(salary, period)}
                />
              ))}
            </ul>
            <div className="flex flex-col gap-2 border-t border-subtle px-4 sm:flex-row sm:items-center sm:justify-between">
              <span className="pt-3 text-meta text-secondary">Showing {(salaryPage - 1) * SALARY_PAGE_SIZE + 1}-{Math.min(salaryPage * SALARY_PAGE_SIZE, salaries.length)} of {salaries.length} salaries</span>
              <Pagination page={salaryPage} totalPages={salaryTotalPages} onPageChange={setSalaryPage} label={`Salaries for ${name}`} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}

/** To pay — released salaries still to hand over or be confirmed, salary by salary. */
export default function PayrollToPay() {
  const { periods, loading, reload, setNotice, canRecordPayments, isOwner } = useOutletContext();
  const [params, setParams] = useSearchParams();
  const [entries, setEntries] = useState({}); // periodId -> { state, data }
  const [dialog, setDialog] = useState(null); // { kind, salary?, period }
  const [page, setPage] = useState(1);

  const released = useMemo(() => periods.filter((row) => periodStatus(row) === "released"), [periods]);
  const filterId = params.get("period");
  const filtered = filterId ? released.filter((row) => String(row.id) === filterId) : released;
  const unknownFilter = Boolean(filterId) && !loading && filtered.length === 0;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PERIOD_PAGE_SIZE));
  const visiblePeriods = filtered.slice((page - 1) * PERIOD_PAGE_SIZE, page * PERIOD_PAGE_SIZE);

  useEffect(() => { setPage(1); }, [filterId]);
  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const loadSalaries = useCallback(async (periodId) => {
    setEntries((prev) => ({ ...prev, [periodId]: { ...prev[periodId], state: prev[periodId]?.data ? "ready" : "loading" } }));
    try {
      const res = await financeAPI.listPeriodSalaries(periodId);
      setEntries((prev) => ({ ...prev, [periodId]: { state: "ready", data: res?.data || null } }));
    } catch {
      setEntries((prev) => ({ ...prev, [periodId]: { state: "error", data: null } }));
    }
  }, []);

  const visibleIds = visiblePeriods.map((row) => row.id).join(",");
  useEffect(() => {
    visiblePeriods.forEach((row) => {
      if (!entries[row.id]) loadSalaries(row.id);
    });
    // load what is newly visible; reloads after actions are explicit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleIds, loadSalaries]);

  const refreshAfter = async (periodId, text) => {
    setDialog(null);
    await Promise.all([loadSalaries(periodId), reload()]);
    setNotice({ tone: "success", text });
  };

  if (loading) return <SkeletonRows rows={4} columns={4} label="Loading salaries to pay" />;

  if (!released.length) {
    return (
      <div className="rounded-panel border border-default bg-surface">
        <EmptyState title="No salaries to pay" description="Released salaries that need payment will appear here." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <p className="max-w-[70ch] text-body-sm text-secondary">
          Record each handover when you give the cash. A salary is paid when the employee confirms receipt in My HR.
        </p>
        {released.length > 1 || filterId ? (
          <label className="flex flex-col gap-1.5 sm:w-64">
            <span className="text-label text-secondary">Show payroll</span>
            <Select
              value={filterId || ""}
              onChange={(event) => {
                const next = event.target.value;
                setParams(next ? { period: next } : {});
              }}
            >
              <option value="">All released payroll</option>
              {released.map((row) => <option key={row.id} value={String(row.id)}>{periodName(row)}</option>)}
            </Select>
          </label>
        ) : null}
      </div>

      {unknownFilter ? (
        <Notice tone="neutral" action={<Link to={PAYMENTS_PATH} className={cn("rounded-control font-medium text-accent hover:underline", focusRing)}>Show all</Link>}>
          That payroll has no salaries to pay here. It may already be settled — see History.
        </Notice>
      ) : null}

      {visiblePeriods.map((period) => (
        <PeriodSalaries
          key={period.id}
          period={period}
          entry={entries[period.id]}
          canRecordPayments={canRecordPayments}
          isOwner={isOwner}
          onRetry={loadSalaries}
          onHandover={(salary, row) => setDialog({ kind: "handover", salary, period: row })}
          onBehalf={(salary, row) => setDialog({ kind: "behalf", salary, period: row })}
          onSettle={(row) => setDialog({ kind: "settle", period: row })}
        />
      ))}

      {!unknownFilter && filtered.length ? (
        <div className="flex flex-col gap-2 border-t border-subtle sm:flex-row sm:items-center sm:justify-between">
          <span className="pt-3 text-meta text-secondary">Showing {(page - 1) * PERIOD_PAGE_SIZE + 1}-{Math.min(page * PERIOD_PAGE_SIZE, filtered.length)} of {filtered.length} payroll periods</span>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} label="Payroll periods pagination" />
        </div>
      ) : null}

      {dialog?.kind === "handover" ? (
        <HandoverDialog
          salary={dialog.salary}
          period={dialog.period}
          recordHandover={financeAPI.recordCashHandover}
          onClose={() => setDialog(null)}
          onRecorded={(salary) => refreshAfter(dialog.period.id,
            `Handover recorded for ${salary.employeeName}. It shows as waiting until they confirm in My HR.`)}
        />
      ) : null}
      {dialog?.kind === "behalf" ? (
        <OnBehalfDialog
          salary={dialog.salary}
          confirmOnBehalf={financeAPI.confirmSalaryOnBehalf}
          onClose={() => setDialog(null)}
          onConfirmed={(salary) => refreshAfter(dialog.period.id, `${salary.employeeName}'s salary is confirmed as paid, on their behalf.`)}
        />
      ) : null}
      {dialog?.kind === "settle" ? (
        <PeriodDecisionDialog
          mode="settle"
          period={dialog.period}
          onClose={() => setDialog(null)}
          onSubmit={async (remarks, doneMessage) => {
            await financeAPI.settlePayrollPeriod(dialog.period.id, { remarks });
            setDialog(null);
            // A settled payroll leaves To pay; keep the operator on the remaining work.
            if (filterId === String(dialog.period.id)) setParams({});
            await reload();
            setNotice({ tone: "success", text: `${doneMessage} It is now under History.` });
          }}
        />
      ) : null}
    </div>
  );
}

